import React, { useEffect, useMemo, useState } from "react";
import "./SendMessage.css";
import MultiSelect from "../../../../components/multi-select/MultiSelect";
import { useAppDispatch, useAppSelector } from "../../../../redux/store/hooks";
import { sendCmsMessage } from "../../../../redux/slices/cmsMessageSlice";
import { searchUsers } from "../../../../redux/slices/userListSlice";

interface SendMessageProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type Member = {
  id: string;
  name: string;
};

const SendMessage: React.FC<SendMessageProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();

  const { searchResults, searchLoading } = useAppSelector(
    (state) => state.userList,
  );
  const { groups } = useAppSelector((state) => state.groups);
  const { createLoading } = useAppSelector((state) => state.cmsMessages);

  const [mode, setMode] = useState<"users" | "group">("users");
  const [selected, setSelected] = useState<Member[]>([]);
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // --------------------------------
  // 🔍 SEARCH TRIGGER
  // --------------------------------
  useEffect(() => {
    if (mode !== "users") return;

    if (searchTerm.trim().length < 3) return;

    const timer = setTimeout(() => {
      dispatch(searchUsers(searchTerm));
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, mode, dispatch]);

  // --------------------------------
  // OPTIONS
  // --------------------------------
  const userOptions: Member[] = useMemo(() => {
    if (searchTerm.trim().length < 3) return [];

    return (
      searchResults?.map((u) => ({
        id: u.id,
        name: `${u.firstName} ${u.lastName}`,
      })) || []
    );
  }, [searchResults, searchTerm]);

  const groupOptions: Member[] = useMemo(
    () =>
      groups.map((g) => ({
        id: g.id,
        name: g.name,
      })),
    [groups],
  );

  const availableOptions = mode === "users" ? userOptions : groupOptions;

  // --------------------------------
  // SEND HANDLER
  // --------------------------------
  const handleSend = async () => {
    if (!message.trim() || selected.length === 0) return;

    const userMap = new Map<string, { id: string; isActive: string }>();

    if (mode === "users") {
      selected.forEach((user) => {
        userMap.set(user.id, {
          id: String(user.id),
          isActive: "true",
        });
      });
    } else {
      selected.forEach((group) => {
        const fullGroup = groups.find((g) => g.id === group.id);

        fullGroup?.user?.forEach((u: any) => {
          userMap.set(u.id, {
            id: u.id,
            isActive: "true",
          });
        });
      });
    }

    const usersPayload = Array.from(userMap.values());

    try {
      await dispatch(
        sendCmsMessage({
          message: message.trim(),
          users: usersPayload,
        }),
      ).unwrap();

      setMessage("");
      setSelected([]);
      setSearchTerm("");

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Send message failed:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="sm-overlay">
      <div className="sm-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="sm-title">Send Message</h2>

        <div className="sm-content">
          <div className="sm-radio-group">
            <label>
              <input
                type="radio"
                value="users"
                checked={mode === "users"}
                onChange={() => {
                  setMode("users");
                  setSelected([]);
                  setSearchTerm("");
                }}
              />
              Users
            </label>

            <label>
              <input
                type="radio"
                value="group"
                checked={mode === "group"}
                onChange={() => {
                  setMode("group");
                  setSelected([]);
                  setSearchTerm("");
                }}
              />
              Group
            </label>
          </div>

          <MultiSelect
            allMembers={availableOptions}
            selectedMembers={selected}
            onChange={setSelected}
            onSearchChange={(val) => setSearchTerm(val)} // ✅ KEY LINE
            label={mode === "users" ? "Select Users" : "Select Group"}
            searchType={mode === "users" ? "Members" : "Groups"}
            loading={mode === "users" ? searchLoading : false}
            placeHolder={
              mode === "users" ? "Search users (min 3 chars)" : "Search Groups"
            }
          />

          <label className="sm-label">
            Message <span className="required">*</span>
          </label>

          <textarea
            className="sm-textarea"
            placeholder="Enter Message"
            value={message}
            required
            onChange={(e) => setMessage(e.target.value)}
            maxLength={250}
          />
          <span className="questionModal-questionLength">{`${message.length}/250`}</span>
        </div>

        <div className="sm-actions">
          <button className="sm-cancel" onClick={onClose}>
            Cancel
          </button>

          <button
            className="sm-send"
            onClick={handleSend}
            disabled={createLoading || selected.length === 0 || !message.trim()}
          >
            {createLoading ? "Sending..." : "Send Message"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendMessage;
